import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Search, Filter, RotateCcw,
  Globe, Languages, User, Calendar, Award, Target, Package,
  Mic2,
} from 'lucide-react';
import VoiceCardExpanded from './VoiceCardExpanded';
import FilterPill from './FilterPill';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  facetCounts,
  matchesFilters,
  matchesSearch,
  translate,
  DIMENSIONS,
  FACET_NONE,
  GENDER_PT,
  AGE_PT,
  CATEGORY_PT,
  ACCENT_PT,
  USE_CASE_PT,
  LANGUAGE_PT,
  PROVIDER_PT,
} from '../../utils/voiceMetadata';
import { useVoicePreview } from '../../contexts/VoicePreviewContext';

// Ícones lucide pros grupos (cor cyan #06B6D4)
const ICON_SIZE = 14;
const GROUP_ICONS = {
  language: <Globe size={ICON_SIZE} />,
  accent: <Languages size={ICON_SIZE} />,
  gender: <User size={ICON_SIZE} />,
  age: <Calendar size={ICON_SIZE} />,
  category: <Award size={ICON_SIZE} />,
  use_case: <Target size={ICON_SIZE} />,
  provider: <Package size={ICON_SIZE} />,
};

/**
 * VoiceLibraryModal — modal fullscreen com sidebar de filtros (faceted) +
 * grade de cards expandidos.
 *
 * Z-index 1100 (acima do swap modal 1000).
 *
 * Props:
 *   - voices, favorites, onSelectVoice, onToggleFavorite, onClose,
 *     renderAvatar(voice, size)
 *
 * Player de preview por idioma é INTERNO (audioRef local) e compartilhado
 * entre todos os cards expandidos — clicar no dropdown de um card pausa o
 * preview de outro.
 */
export default function VoiceLibraryModal({
  voices,
  favorites,
  onSelectVoice,
  onToggleFavorite,
  onClose,
  renderAvatar,
}) {
  // Preview de voz pelo player COMPARTILHADO (mesma barra inferior estilo
  // ElevenLabs da página). Clicar num idioma toca/alterna no player único.
  const vp = useVoicePreview();
  const handlePlayLanguage = (voice, language, accent, previewUrl, key) => {
    vp.play(voice, { language, accent, previewUrl, key });
  };
  const [filters, setFilters] = useState(() => ({
    language: new Set(),
    accent: new Set(),
    gender: new Set(),
    age: new Set(),
    category: new Set(),
    use_case: new Set(),
    provider: new Set(),
  }));
  const [search, setSearch] = useState('');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  // ITER3.1: nome da pill atualmente aberta (só 1 aberta de cada vez)
  const [openPillName, setOpenPillName] = useState(null);

  // ESC fecha
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleFilter = (dim, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      const set = new Set(next[dim]);
      if (set.has(value)) set.delete(value); else set.add(value);
      next[dim] = set;
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilters({
      language: new Set(),
      accent: new Set(),
      gender: new Set(),
      age: new Set(),
      category: new Set(),
      use_case: new Set(),
      provider: new Set(),
    });
    setSearch('');
    setOnlyFavorites(false);
  };

  const totalActiveFilters = useMemo(() => {
    let n = 0;
    for (const d of DIMENSIONS) n += filters[d].size;
    if (search.trim()) n += 1;
    if (onlyFavorites) n += 1;
    return n;
  }, [filters, search, onlyFavorites]);

  // 7 contagens (1 useMemo por dimensão) — recálculo só quando filters/voices mudam
  const languageCounts = useMemo(() => facetCounts(voices, filters, 'language'), [voices, filters]);
  const accentCounts   = useMemo(() => facetCounts(voices, filters, 'accent'),   [voices, filters]);
  const genderCounts   = useMemo(() => facetCounts(voices, filters, 'gender'),   [voices, filters]);
  const ageCounts      = useMemo(() => facetCounts(voices, filters, 'age'),      [voices, filters]);
  const categoryCounts = useMemo(() => facetCounts(voices, filters, 'category'), [voices, filters]);
  const useCaseCounts  = useMemo(() => facetCounts(voices, filters, 'use_case'), [voices, filters]);
  const providerCounts = useMemo(() => facetCounts(voices, filters, 'provider'), [voices, filters]);

  // Vozes filtradas (busca inteligente + favoritos + facets)
  const filteredVoices = useMemo(() => {
    return voices.filter((v) => {
      if (onlyFavorites && !(favorites || []).includes(v.id)) return false;
      if (!matchesFilters(v, filters)) return false;
      if (!matchesSearch(v, search)) return false;
      return true;
    });
  }, [voices, filters, search, onlyFavorites, favorites]);

  // PERFORMANCE — virtualização da GRADE (não monta as ~575 vozes de uma vez).
  // Mesmo padrão da lista principal (commit 931e9df2), adaptado a GRADE: virtualiza
  // por LINHAS; cada linha é uma grade de N colunas. N é a MESMA contagem do
  // auto-fill original (replica a fórmula a partir da largura, via ResizeObserver),
  // então o layout fica idêntico. Altura dinâmica por linha (measureElement).
  const GRID_MIN_CARD = 320;   // minmax(320px, 1fr) do layout original
  const GRID_GAP = 12;         // gap do grid original
  const GRID_PAD = 14;         // padding do <main> (scroll container)
  const gridScrollRef = useRef(null);
  const [columnsPerRow, setColumnsPerRow] = useState(3);

  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el) return;
    const recompute = () => {
      const contentW = el.clientWidth - GRID_PAD * 2;           // largura útil da grade
      // Mesma conta do CSS auto-fill: floor((W + gap) / (minCol + gap)).
      const cols = Math.max(1, Math.floor((contentW + GRID_GAP) / (GRID_MIN_CARD + GRID_GAP)));
      setColumnsPerRow((prev) => (prev === cols ? prev : cols));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rowCount = Math.ceil(filteredVoices.length / columnsPerRow);
  const gridRowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => gridScrollRef.current,
    estimateSize: () => 200,
    overscan: 3,
    getItemKey: (rowIndex) => filteredVoices[rowIndex * columnsPerRow]?.id ?? rowIndex,
  });

  // ITER3.1: dois conjuntos de grupos
  // - PILL_GROUPS: filtros com muitas opções vão pra pills horizontais no topo
  // - SIDEBAR_GROUPS: filtros curtos vão pra sidebar enxuta
  const PILL_GROUPS = [
    { key: 'language', icon: GROUP_ICONS.language, label: 'Idioma',  dict: LANGUAGE_PT, counts: languageCounts },
    { key: 'accent',   icon: GROUP_ICONS.accent,   label: 'Sotaque', dict: ACCENT_PT,   counts: accentCounts },
    { key: 'gender',   icon: GROUP_ICONS.gender,   label: 'Gênero',  dict: GENDER_PT,   counts: genderCounts },
  ];
  const SIDEBAR_GROUPS = [
    { key: 'category', icon: GROUP_ICONS.category, label: 'Categoria', dict: CATEGORY_PT, counts: categoryCounts },
    { key: 'age',      icon: GROUP_ICONS.age,      label: 'Idade',     dict: AGE_PT,      counts: ageCounts },
    { key: 'use_case', icon: GROUP_ICONS.use_case, label: 'Uso',       dict: USE_CASE_PT, counts: useCaseCounts },
    { key: 'provider', icon: GROUP_ICONS.provider, label: 'Provedor',  dict: PROVIDER_PT, counts: providerCounts },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        // Tokens premium (= página de áudio / .app-card da vendas): override LOCAL
        // pra TODO o modal herdar card #141414 em vez do #242424 global.
        '--dp-bg-card': '#141414',
        '--dp-bg-card-hover': '#1A1A1A',
        '--dp-bg-input': '#0E0E0E',
        '--dp-bg-secondary': '#0E0E0E',
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'agFadeIn 0.2s ease-out',
        fontFamily: 'var(--dp-font)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1280px',
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(13,174,255,0.10)',
        }}
      >
        {/* ─── HEADER ─── */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--dp-text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
          }}>
            <Mic2 size={20} style={{ color: '#06B6D4', flexShrink: 0 }} />
            Biblioteca de Vozes
            <span style={{
              fontSize: '11px',
              fontWeight: 500,
              color: 'var(--dp-text-tertiary)',
              background: 'var(--dp-bg-primary)',
              padding: '2px 8px',
              borderRadius: '9999px',
              border: '1px solid var(--dp-border-subtle)',
            }}>
              {filteredVoices.length} / {voices.length}
            </span>
          </div>

          {/* Busca */}
          <div style={{ position: 'relative', flex: 1, maxWidth: '420px' }}>
            <Search size={14} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--dp-text-tertiary)',
            }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, descrição, slug..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 32px',
                background: 'var(--dp-bg-input, var(--dp-bg-primary))',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px',
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
                fontSize: '12px',
                outline: 'none',
                transition: 'all 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.20)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* "Minhas Vozes" — toggle de favoritos do usuário */}
          <button
            onClick={() => setOnlyFavorites((v) => !v)}
            title="Mostrar só as vozes favoritadas"
            style={{
              padding: '7px 12px',
              borderRadius: '9999px',
              border: onlyFavorites ? '1px solid rgba(248,113,113,0.4)' : '1px solid var(--dp-border-subtle)',
              background: onlyFavorites ? 'rgba(248,113,113,0.10)' : 'transparent',
              color: onlyFavorites ? '#F87171' : 'var(--dp-text-secondary)',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            ♡ Minhas Vozes
          </button>

          {/* Limpar */}
          {totalActiveFilters > 0 && (
            <button
              onClick={clearAllFilters}
              title="Limpar filtros"
              style={{
                padding: '7px 12px',
                borderRadius: '9999px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'transparent',
                color: 'var(--dp-text-secondary)',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 500,
                fontFamily: 'var(--dp-font)',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap',
              }}
            >
              <RotateCcw size={11} />
              Limpar ({totalActiveFilters})
            </button>
          )}

          {/* Fechar */}
          <button
            onClick={onClose}
            title="Fechar"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(248,113,113,0.4)'; e.currentTarget.style.color = '#F87171'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ─── ITER3.1: PILLS BAR — filtros com muitas opções (horizontal) ─── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 20px 12px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          flexWrap: 'wrap',
          flexShrink: 0,
          background: 'var(--dp-bg-secondary)',
        }}>
          <span style={{
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: 'var(--dp-text-tertiary)',
            marginRight: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
          }}>
            <Filter size={11} /> Filtros rápidos:
          </span>
          {PILL_GROUPS.map((group) => (
            <FilterPill
              key={group.key}
              icon={group.icon}
              label={group.label}
              group={group}
              activeSet={filters[group.key]}
              onToggle={(value) => toggleFilter(group.key, value)}
              isOpen={openPillName === group.key}
              onSetOpen={(open) => setOpenPillName(open ? group.key : null)}
            />
          ))}
        </div>

        {/* ─── BODY: 2 colunas (sidebar enxuta + grid de cards) ─── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '200px minmax(0, 1fr)',
          flex: 1,
          overflow: 'hidden',
        }}>
          {/* SIDEBAR — filtros curtos (categoria, idade, uso) */}
          <aside style={{
            borderRight: '1px solid var(--dp-border-subtle)',
            overflowY: 'auto',
            padding: '14px 12px',
            background: 'var(--dp-bg-secondary)',
          }} className="ag-scrollbar">
            <div style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: 'var(--dp-text-tertiary)',
              padding: '0 4px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <Filter size={11} /> Refinar
            </div>
            {SIDEBAR_GROUPS.map((group) => (
              <FilterGroup
                key={group.key}
                group={group}
                activeSet={filters[group.key]}
                onToggle={(value) => toggleFilter(group.key, value)}
              />
            ))}
          </aside>

          {/* GRID DE CARDS */}
          <main ref={gridScrollRef} style={{
            overflowY: 'auto',
            padding: '14px',
          }} className="ag-scrollbar">
            {filteredVoices.length === 0 ? (
              <div style={{
                padding: '60px 20px',
                textAlign: 'center',
                color: 'var(--dp-text-tertiary)',
                fontSize: '13px',
              }}>
                Nenhuma voz corresponde aos filtros aplicados.
                <br />
                <button onClick={clearAllFilters} style={{
                  marginTop: '12px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'transparent',
                  color: 'var(--dp-blue-500)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontFamily: 'var(--dp-font)',
                }}>Limpar filtros</button>
              </div>
            ) : (
              // Container de altura total (todas as linhas); linhas visíveis posicionadas
              // por translateY. Cada linha é a MESMA grade de N colunas do auto-fill.
              <div style={{ height: `${gridRowVirtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}>
                {gridRowVirtualizer.getVirtualItems().map((vRow) => {
                  const start = vRow.index * columnsPerRow;
                  const rowVoices = filteredVoices.slice(start, start + columnsPerRow);
                  return (
                    <div
                      key={vRow.key}
                      data-index={vRow.index}
                      ref={gridRowVirtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${vRow.start}px)`,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columnsPerRow}, minmax(0, 1fr))`,
                        gap: '12px',
                        paddingBottom: '12px',  // replica o gap entre linhas (entra na medição)
                      }}
                    >
                      {rowVoices.map((voice) => (
                        <VoiceCardExpanded
                          key={voice.id}
                          voice={voice}
                          isFavorite={(favorites || []).includes(voice.id)}
                          onToggleFavorite={onToggleFavorite}
                          onSelectVoice={onSelectVoice}
                          onPlayLanguage={handlePlayLanguage}
                          currentLanguageKey={vp.activeKeyFor(voice.id)}
                          avatar={renderAvatar ? renderAvatar(voice, 36) : null}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-componente: grupo de filtros (colapsável + "Ver todos") ─────────

const VISIBLE_LIMIT = 8;

function FilterGroup({ group, activeSet, onToggle }) {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false); // "Ver todos" estendido

  // Separa entries normais (não-__none__) das entradas "Outros" (__none__)
  const allEntries = Object.entries(group.counts || {}).filter(([, count]) => count > 0);
  const normalEntries = allEntries
    .filter(([key]) => key !== FACET_NONE)
    .sort((a, b) => {
      const aActive = activeSet.has(a[0]);
      const bActive = activeSet.has(b[0]);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return b[1] - a[1]; // count desc
    });
  const noneEntry = allEntries.find(([key]) => key === FACET_NONE) || null;

  // Mantém entradas ativas SEMPRE visíveis (sobem pro topo via sort acima)
  const hasMoreThanLimit = normalEntries.length > VISIBLE_LIMIT;
  const visibleNormal = (expanded || !hasMoreThanLimit)
    ? normalEntries
    : normalEntries.slice(0, VISIBLE_LIMIT);

  if (normalEntries.length === 0 && !noneEntry && activeSet.size === 0) return null;

  return (
    <div style={{ marginBottom: '14px' }}>
      <button
        onClick={() => setCollapsed((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 4px',
          background: 'transparent',
          border: 'none',
          color: 'var(--dp-text-secondary)',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          fontFamily: 'var(--dp-font)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ display: 'inline-flex', color: '#06B6D4', flexShrink: 0 }}>{group.icon}</span>
          {group.label}
          {activeSet.size > 0 && (
            <span style={{
              fontSize: '9px',
              fontWeight: 700,
              color: 'var(--dp-blue-500)',
              background: 'rgba(13,174,255,0.14)',
              padding: '1px 6px',
              borderRadius: '9999px',
            }}>{activeSet.size}</span>
          )}
        </span>
        <span style={{ opacity: 0.5, fontSize: '12px' }}>{collapsed ? '+' : '−'}</span>
      </button>
      {!collapsed && (
        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {visibleNormal.map(([value, count]) => (
            <FilterRow
              key={value}
              label={translate(value, group.dict)}
              count={count}
              checked={activeSet.has(value)}
              onToggle={() => onToggle(value)}
            />
          ))}

          {/* Botão "Ver todos / menos" — só se passar do limite */}
          {hasMoreThanLimit && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                marginTop: '2px',
                padding: '5px 8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--dp-blue-500)',
                cursor: 'pointer',
                fontSize: '10.5px',
                fontWeight: 600,
                fontFamily: 'var(--dp-font)',
                textAlign: 'left',
                letterSpacing: '0.2px',
              }}
            >
              {expanded ? '− Ver menos' : `+ Ver todos (${normalEntries.length})`}
            </button>
          )}

          {/* Entrada "Outros" — vozes sem voice_info pra essa dimensão */}
          {noneEntry && (
            <>
              <div style={{
                height: '1px',
                background: 'var(--dp-border-subtle)',
                margin: '4px 6px',
                opacity: 0.5,
              }} />
              <FilterRow
                label="Outros"
                count={noneEntry[1]}
                checked={activeSet.has(FACET_NONE)}
                onToggle={() => onToggle(FACET_NONE)}
                muted
                title="Vozes sem metadados (CapCut, Multivoz, etc)"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FilterRow({ label, count, checked, onToggle, muted = false, title }) {
  const activeColor = muted ? 'var(--dp-text-primary)' : 'var(--dp-text-primary)';
  const baseColor = muted ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)';
  const activeBg = muted ? 'rgba(148,163,184,0.10)' : 'rgba(13,174,255,0.06)';
  const accentColor = muted ? '#94A3B8' : 'var(--dp-blue-500)';
  return (
    <label
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 8px',
        borderRadius: '6px',
        cursor: 'pointer',
        background: checked ? activeBg : 'transparent',
        transition: 'background 0.15s',
        fontSize: '11px',
        color: checked ? activeColor : baseColor,
        fontStyle: muted ? 'italic' : 'normal',
      }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)'; }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{
          width: '13px',
          height: '13px',
          accentColor,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', flexShrink: 0 }}>{count}</span>
    </label>
  );
}
